import ResourcePage from "@/components/ResourcePage";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";

const STATUS_OPTIONS = [
  { value: "interessent", label: "Interessent" },
  { value: "kunde", label: "Kunde" },
  { value: "partner", label: "Partner" },
  { value: "inaktiv", label: "Inaktiv" },
];

const STATUS_BADGE = {
  interessent: "bg-amber-50 text-amber-700 border-amber-200",
  kunde: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partner: "bg-violet-50 text-violet-700 border-violet-200",
  inaktiv: "bg-gray-100 text-gray-500 border-gray-200",
};

const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s.label]));

function CompanyStatusBadge({ value }) {
  if (!value) return <span className="text-gray-400">—</span>;
  const cls = STATUS_BADGE[value] || "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border ${cls}`}
      data-testid={`company-status-badge-${value}`}
    >
      {STATUS_LABEL[value] || value}
    </span>
  );
}

const COUNTRY_OPTIONS = [
  { value: "Deutschland", label: "Deutschland" },
  { value: "Österreich", label: "Österreich" },
  { value: "Schweiz", label: "Schweiz" },
  { value: "Niederlande", label: "Niederlande" },
  { value: "Belgien", label: "Belgien" },
  { value: "Frankreich", label: "Frankreich" },
  { value: "Italien", label: "Italien" },
  { value: "Spanien", label: "Spanien" },
  { value: "Polen", label: "Polen" },
  { value: "Tschechien", label: "Tschechien" },
  { value: "Dänemark", label: "Dänemark" },
  { value: "Luxemburg", label: "Luxemburg" },
  { value: "USA", label: "USA" },
  { value: "Vereinigtes Königreich", label: "Vereinigtes Königreich" },
];

async function lookupPostal(plz, setField) {
  const trimmed = (plz || "").trim();
  if (!/^\d{5}$/.test(trimmed)) return;
  try {
    const { data } = await api.get(`/postal-lookup/${trimmed}`);
    if (data?.city) {
      // Always suggest city when PLZ resolves; we overwrite to avoid stale mismatches.
      setField("city", data.city);
      if (data.country) setField("country", data.country);
    }
  } catch {
    // ignore - silent suggestion failure
  }
}

export default function CompaniesPage() {
  const navigate = useNavigate();
  return (
    <ResourcePage
      resource="companies"
      title="Firmen"
      subtitle="Alle Unternehmen, mit denen ihr in Kontakt seid."
      singular="Firma"
      newButtonLabel="Firma anlegen"
      createDialogTitle="Firma anlegen"
      editDialogTitle="Firma bearbeiten"
      submitButtonLabel="Firma speichern"
      emptyDescription="Lege deine erste Firma an."
      emptyCreateButtonLabel="Firma anlegen"
      testidPrefix="companies"
      defaultValues={{ status: "interessent", country: "Deutschland" }}
      onRowClick={(row) => navigate(`/companies/${row.id}`)}
      fields={[
        { name: "name", label: "Firmenname", type: "text", required: true },
        { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS },
        { name: "industry", label: "Branche", type: "text" },
        { name: "website", label: "Website", type: "text", placeholder: "https://…" },
        { name: "email", label: "E-Mail", type: "email" },
        { name: "phone", label: "Telefon", type: "tel" },
        { name: "street", label: "Straße", type: "text" },
        { name: "house_number", label: "Hausnummer", type: "text" },
        {
          name: "postal_code",
          label: "PLZ",
          type: "text",
          placeholder: "z. B. 10115",
          helperText: "Bei deutschen PLZ wird der Ort automatisch vorgeschlagen.",
          onValueChange: (value, setField) => {
            // Trigger lookup as soon as 5 digits entered
            if (/^\d{5}$/.test((value || "").trim())) {
              lookupPostal(value, setField);
            }
          },
          onBlur: (value, setField) => lookupPostal(value, setField),
        },
        { name: "city", label: "Ort", type: "text" },
        { name: "country", label: "Land", type: "select", options: COUNTRY_OPTIONS, placeholder: "Land wählen…" },
        {
          name: "primary_contact_id",
          label: "Hauptansprechpartner",
          type: "select",
          optionsFromResource: "contacts",
          optionLabel: (c) =>
            `${c.first_name} ${c.last_name}${c.position ? ` · ${c.position}` : ""}`,
          optionsFilter: (c, { editing }) => {
            // Only show contacts belonging to this company (after creation).
            if (!editing) return false;
            return c.company_id === editing.id;
          },
          placeholder: "Kontakt wählen…",
          helperText:
            "Nur Kontakte dieser Firma. Lege ggf. zuerst Kontakte an und weise sie der Firma zu.",
          fullWidth: true,
        },
        { name: "notes", label: "Notizen", type: "textarea", fullWidth: true },
      ]}
      columns={[
        {
          key: "name",
          label: "Firma",
          render: (r) => <span className="font-medium text-gray-900">{r.name}</span>,
        },
        {
          key: "status",
          label: "Status",
          render: (r) => <CompanyStatusBadge value={r.status} />,
        },
        { key: "industry", label: "Branche" },
        {
          key: "address_combined",
          label: "Ort",
          render: (r) => {
            const plz = r.postal_code || "";
            const city = r.city || "";
            if (!plz && !city) return <span className="text-gray-400">—</span>;
            return (
              <span>
                {plz ? <span className="text-gray-500">{plz} </span> : null}
                {city}
              </span>
            );
          },
        },
        {
          key: "primary_contact_name",
          label: "Hauptansprechpartner",
          render: (r) =>
            r.primary_contact_name ? (
              <span className="text-gray-900">{r.primary_contact_name}</span>
            ) : (
              <span className="text-gray-400">—</span>
            ),
        },
        { key: "phone", label: "Telefon" },
      ]}
    />
  );
}
